<!-- CamView.svelte -->
<script>
  import { onDestroy, onMount } from 'svelte';

  import ImageGalleryViewer from '@components/ImageGalleryViewer.svelte';
  import {
    loadCameraRecordingSetting,
    normalizeCameraRecordingConfig,
    resetCameraRecordingSetting,
    saveCameraRecordingSetting
  } from '$lib/camera-recording-settings.js';
  import {
    captureCameraFrame,
    createCameraNetworkStateHandler,
    createCameraSyncProgressHandler,
    initializeCameraView,
    openCameraCaptureGallery,
    refreshCameraPendingCount,
    setupCameraRecovery,
    startCameraStreamRecording,
    stopCameraStreamRecording,
    stopCameraViewStream,
    syncCameraRecordingState,
    toggleCameraFacingMode
  } from '$lib/streamer-client.js';

  import CameraControls from './CameraControls.svelte';
  import CameraSettingsPanel from './CameraSettingsPanel.svelte';
  import CameraStatusBar from './CameraStatusBar.svelte';

  export let onClose = () => {};

  let videoElement;
  let mediaStream;
  let recorderController;
  let recoveryCleanup;
  let cameraConfig = normalizeCameraRecordingConfig();
  let draftCameraConfig = normalizeCameraRecordingConfig();
  let settingsOpen = false;
  let settingsLoading = true;
  let settingsSaving = false;
  let settingsStatus = '';
  let settingsError = '';

  let facingMode = 'environment';
  let recording = false;
  let stopping = false;
  let online = typeof navigator === 'undefined' ? true : navigator.onLine;
  let recordingStatus = 'Sẵn sàng';
  let pendingChunks = 0;

  let captures = [];
  let galleryOpen = false;
  let galleryIndex = 0;

  $: latestCapture = captures[0] || null;
  $: galleryImages = captures.map((capture) => capture.url);
  $: profileOptions = cameraConfig.availableProfiles || [];
  $: resolutionOptions = cameraConfig.availableResolutionPresets || [];
  $: frameRateOptions = cameraConfig.availableFrameRates || [];
  $: segmentOptions = cameraConfig.availableSegmentDurations || [];
  $: mimeOptions = cameraConfig.availableMimeTypes || [];
  $: recordingTransportOptions = cameraConfig.availableRecordingTransportModes || [];
  $: formatOptions = cameraConfig.supportedOutputFormats || [];
  $: ffmpegPresetOptions = cameraConfig.supportedFfmpegPresets || [];
  $: activeCameraConfig = settingsOpen ? draftCameraConfig : cameraConfig;
  $: showSyncControl = activeCameraConfig?.recording?.transportMode === 'http-chunk';
  $: showGridlines = activeCameraConfig?.ui?.showGridlines ?? true;
  $: settingsDirty = JSON.stringify(draftCameraConfig) !== JSON.stringify(cameraConfig);

  const PROFILE_OVERRIDES = {
    standard: {},
    quality: {
      capture: {
        resolution: '1920x1080',
        frameRate: 30,
        audioEnabled: true
      },
      recording: {
        segmentDurationMs: 5000,
        videoBitsPerSecond: 5000000,
        audioBitsPerSecond: 192000
      },
      output: {
        format: 'mp4'
      },
      ffmpeg: {
        preset: 'faster',
        crf: 21
      }
    },
    compact: {
      capture: {
        resolution: '1280x720',
        frameRate: 25,
        audioEnabled: false
      },
      recording: {
        segmentDurationMs: 3000,
        videoBitsPerSecond: 1200000,
        audioBitsPerSecond: 96000
      },
      output: {
        format: 'webm'
      },
      ffmpeg: {
        preset: 'veryfast',
        crf: 30
      }
    }
  };

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function merge(base, override) {
    if (!override || typeof override !== 'object' || Array.isArray(override)) return override ?? base;

    const result = { ...(base || {}) };
    for (const [key, value] of Object.entries(override)) {
      result[key] = value && typeof value === 'object' && !Array.isArray(value)
        ? merge(base?.[key], value)
        : value;
    }
    return result;
  }

  function syncSettingsState(config) {
    cameraConfig = normalizeCameraRecordingConfig(config);
    draftCameraConfig = clone(cameraConfig);
  }

  function handleSettingsChange() {
    draftCameraConfig = clone(draftCameraConfig);
  }

  const handleSyncProgress = createCameraSyncProgressHandler({
    getOnline: () => online,
    getRecording: () => recording,
    setRecordingStatus: (value) => {
      recordingStatus = value;
    },
    refreshPendingCount: () => refreshPendingCount()
  });

  onMount(() => {
    let disposed = false;
    const updateNetworkState = createCameraNetworkStateHandler((value) => {
      online = value;
    });

    window.addEventListener('online', updateNetworkState);
    window.addEventListener('offline', updateNetworkState);

    recordingStatus = 'Đang tải cấu hình camera';

    (async () => {
      try {
        const setting = await loadCameraRecordingSetting();
        if (disposed) return;
        syncSettingsState(setting?.effective);
      } catch (error) {
        console.warn('Khong tai duoc cau hinh camera, dang dung mac dinh', error);
        if (disposed) return;
        syncSettingsState(normalizeCameraRecordingConfig());
        settingsError = 'Không tải được cấu hình camera, đang dùng mặc định.';
      } finally {
        if (disposed) return;
        settingsLoading = false;
        recoveryCleanup = setupCameraRecovery(handleSyncProgress, () => refreshPendingCount(), () => initCamera());
      }
    })();

    return () => {
      disposed = true;
      window.removeEventListener('online', updateNetworkState);
      window.removeEventListener('offline', updateNetworkState);
      recoveryCleanup?.();
    };
  });

  onDestroy(() => {
    captures.forEach((capture) => {
      if (capture.localUrl) URL.revokeObjectURL(capture.localUrl);
    });

    if (recorderController) {
      recorderController.stop().finally(() => stopCamera());
    } else {
      stopCamera();
    }
  });

  async function refreshPendingCount() {
    const nextPendingChunks = await refreshCameraPendingCount((value) => {
      pendingChunks = value;
    });

    if (typeof nextPendingChunks === 'number') {
      pendingChunks = nextPendingChunks;
    }
  }

  export async function initCamera(nextFacingMode = facingMode) {
    mediaStream = await initializeCameraView({
      mediaStream,
      videoElement,
      facingMode: nextFacingMode,
      cameraConfig,
      setRecordingStatus: (value) => {
        recordingStatus = value;
      }
    });

    return mediaStream;
  }

  async function toggleCamera() {
    facingMode = await toggleCameraFacingMode({
      recording,
      stopping,
      facingMode,
      initCamera: async (nextFacingMode) => initCamera(nextFacingMode)
    });
  }

  export function stopCamera() {
    mediaStream = stopCameraViewStream(mediaStream, videoElement);
  }

  export async function capture() {
    captures = await captureCameraFrame({
      videoElement,
      captures,
      setRecordingStatus: (value) => {
        recordingStatus = value;
      }
    });
  }

  async function startStream() {
    if (recording || stopping) return;

    const result = await startCameraStreamRecording({
      mediaStream,
      cameraConfig,
      initCamera: () => initCamera(),
      getOnline: () => online,
      refreshPendingCount: () => refreshPendingCount(),
      handleSyncProgress,
      setRecordingStatus: (value) => {
        recordingStatus = value;
      }
    });

    mediaStream = result.mediaStream ?? mediaStream;
    recorderController = result.recorderController;

    if (recorderController) {
      recording = true;
    }
  }

  async function stopStream() {
    if (!recorderController || stopping) return;

    stopping = true;

    const result = await stopCameraStreamRecording({
      recorderController,
      pendingChunks,
      refreshPendingCount: () => refreshPendingCount(),
      setRecordingStatus: (value) => {
        recordingStatus = value;
      }
    });

    recorderController = result.recorderController;
    recording = result.recording;
    stopping = result.stopping;
  }

  export async function startRecording() {
    return startStream();
  }

  export async function stopRecording() {
    return stopStream();
  }

  export async function startWebSocketStream() {
    return startStream();
  }

  export async function stopWebSocketStream() {
    return stopStream();
  }

  function openGallery(index = 0) {
    const galleryState = openCameraCaptureGallery(index);
    galleryIndex = galleryState.galleryIndex;
    galleryOpen = galleryState.galleryOpen;
  }

  function syncRecordings() {
    syncCameraRecordingState({
      setRecordingStatus: (value) => {
        recordingStatus = value;
      },
      handleSyncProgress
    });
  }

  function openSettings() {
    settingsOpen = true;
    settingsStatus = '';
    settingsError = '';
    draftCameraConfig = clone(cameraConfig);
  }

  function closeSettings() {
    settingsOpen = false;
    settingsStatus = '';
    settingsError = '';
    draftCameraConfig = clone(cameraConfig);
  }

  function applyProfile(event) {
    const profile = event.detail.profile;
    const overrides = PROFILE_OVERRIDES[profile] || {};

    draftCameraConfig = normalizeCameraRecordingConfig(
      merge(draftCameraConfig, {
        profile,
        ...overrides
      })
    );
    settingsStatus = '';
  }

  async function saveSettings() {
    settingsSaving = true;
    settingsError = '';

    try {
      await saveCameraRecordingSetting(draftCameraConfig);
      const persisted = await loadCameraRecordingSetting();
      syncSettingsState(persisted?.effective);
      settingsStatus = recording
        ? 'Đã lưu cấu hình backend camera. Phiên ghi hiện tại giữ nguyên, phiên sau sẽ dùng thiết lập mới.'
        : 'Đã lưu cấu hình backend camera. Đang làm mới preview để áp dụng ngay.';

      if (!recording && !stopping) {
        await initCamera();
      }
    } catch (error) {
      settingsError = error?.message || 'Không thể lưu cấu hình camera';
    } finally {
      settingsSaving = false;
    }
  }

  async function resetSettings() {
    settingsSaving = true;
    settingsError = '';

    try {
      const nextBundle = await resetCameraRecordingSetting();
      syncSettingsState(nextBundle?.effective);
      settingsStatus = 'Đã đặt lại cấu hình backend camera về mặc định.';

      if (!recording && !stopping) {
        await initCamera();
      }
    } catch (error) {
      settingsError = error?.message || 'Không thể đặt lại cấu hình camera';
    } finally {
      settingsSaving = false;
    }
  }
</script>

<div class="camera-view">
  <video
    bind:this={videoElement}
    autoplay
    playsinline
    muted
    class="camera-preview"
    on:loadedmetadata={() => videoElement.play()}>
  </video>

  <div class="camera-vignette"></div>
  {#if showGridlines}
    <div class="camera-grid" aria-hidden="true">
      <span></span><span></span><span></span><span></span>
    </div>
  {/if}

  <CameraStatusBar {online} {recording} {stopping} status={recordingStatus} {pendingChunks} {onClose} />

  <CameraSettingsPanel
    open={settingsOpen}
    loading={settingsLoading}
    saving={settingsSaving}
    {recording}
    config={draftCameraConfig}
    {profileOptions}
    {resolutionOptions}
    {frameRateOptions}
    {segmentOptions}
    {mimeOptions}
    {recordingTransportOptions}
    {formatOptions}
    ffmpegPresetOptions={ffmpegPresetOptions}
    status={settingsStatus}
    error={settingsError}
    dirty={settingsDirty}
    on:change={handleSettingsChange}
    on:close={closeSettings}
    on:profile={applyProfile}
    on:save={saveSettings}
    on:reset={resetSettings} />

  <CameraControls
    {recording}
    {stopping}
    {pendingChunks}
    showSync={showSyncControl}
    {latestCapture}
    captureCount={captures.length}
    onCapture={capture}
    onStart={startStream}
    onStop={stopStream}
    onSwitch={toggleCamera}
    onSync={syncRecordings}
    onOpenSettings={openSettings}
    onOpenGallery={() => openGallery(0)} />
</div>

{#if galleryOpen}
  <ImageGalleryViewer images={galleryImages} index={galleryIndex} onClose={() => (galleryOpen = false)} />
{/if}

<style>
  .camera-view {
    position: relative;
    isolation: isolate;
    display: flex;
    width: 100%;
    height: 100%;
    min-height: 18rem;
    overflow: hidden;
    background:
      radial-gradient(circle at 50% 20%, rgba(30, 41, 59, 0.7), transparent 55%),
      #020617;
  }

  .camera-preview {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #020617;
  }

  .camera-vignette,
  .camera-grid {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .camera-vignette {
    background:
      linear-gradient(to bottom, rgba(2, 6, 23, 0.42), transparent 24%, transparent 68%, rgba(2, 6, 23, 0.62)),
      radial-gradient(circle, transparent 48%, rgba(2, 6, 23, 0.32));
  }

  .camera-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    opacity: 0.12;
  }

  .camera-grid span:nth-child(1),
  .camera-grid span:nth-child(2) {
    grid-row: 1 / 4;
    border-right: 1px solid white;
  }

  .camera-grid span:nth-child(1) {
    grid-column: 1;
  }

  .camera-grid span:nth-child(2) {
    grid-column: 2;
  }

  .camera-grid span:nth-child(3),
  .camera-grid span:nth-child(4) {
    grid-column: 1 / 4;
    border-bottom: 1px solid white;
  }

  .camera-grid span:nth-child(3) {
    grid-row: 1;
  }

  .camera-grid span:nth-child(4) {
    grid-row: 2;
  }

  @media (max-width: 767px) {
    .camera-preview {
      object-fit: cover;
    }
  }
</style>
